// src/RecipeDB/recetarioService.js
import {
  collection, doc, setDoc, getDoc, getDocs,
  updateDoc, deleteDoc, arrayUnion, arrayRemove,
  query, where, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';

// ── Crear recetario ───────────────────────────────────────────────────
export const createRecetario = async (userId, { nombre, descripcion = '', publica = false }) => {
  const id = uuidv4();
  const data = {
    id,
    nombre: nombre.trim(),
    descripcion: descripcion.trim(),
    ownerId: userId,
    coOwnerIds: [],
    sharedWithIds: [],
    recetaIds: [],
    coverImages: [],
    publica,
    enlaceCompartido: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, 'recetarios', id), data);
  return id;
};

const tsToMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
};

const sortRecetariosDesc = (items) => (
  [...items].sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt))
);

// ── Obtener recetarios del usuario (propios + co-owned + compartidos) ─
export const getRecetariosOfUser = async (userId) => {
  // Cada query en su propio allSettled para que un fallo parcial
  // no oculte los recetarios propios
  const [r1, r2, r3] = await Promise.allSettled([
    getDocs(query(collection(db, 'recetarios'), where('ownerId', '==', userId))),
    getDocs(query(collection(db, 'recetarios'), where('coOwnerIds', 'array-contains', userId))),
    getDocs(query(collection(db, 'recetarios'), where('sharedWithIds', 'array-contains', userId))),
  ]);

  const seen = new Set();
  const merged = [];
  [r1, r2, r3].forEach((result, i) => {
    if (result.status === 'fulfilled') {
      result.value.docs.forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          merged.push({ ...d.data(), id: d.id });
        }
      });
    } else {
      console.warn(`getRecetariosOfUser query ${i + 1} failed:`, result.reason?.message);
    }
  });

  return sortRecetariosDesc(merged);
};

// ── Obtener un recetario por ID ───────────────────────────────────────
export const getRecetario = async (recetarioId) => {
  const snap = await getDoc(doc(db, 'recetarios', recetarioId));
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
};

// ── Añadir receta a recetario ─────────────────────────────────────────
export const addRecetaToRecetario = async (recetarioId, recetaId, coverImageUrl = null) => {
  const ref = doc(db, 'recetarios', recetarioId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const data = snap.data();
  const updates = { recetaIds: arrayUnion(recetaId), updatedAt: serverTimestamp() };

  // Mantener hasta 4 imágenes de portada
  if (coverImageUrl && !data.coverImages?.includes(coverImageUrl)) {
    const newCovers = [...(data.coverImages || []), coverImageUrl].slice(0, 4);
    updates.coverImages = newCovers;
  }

  await updateDoc(ref, updates);
};

// ── Quitar receta de recetario ────────────────────────────────────────
export const removeRecetaFromRecetario = async (recetarioId, recetaId) => {
  await updateDoc(doc(db, 'recetarios', recetarioId), {
    recetaIds: arrayRemove(recetaId),
    updatedAt: serverTimestamp(),
  });
};

// ── Editar recetario ──────────────────────────────────────────────────
export const updateRecetario = async (recetarioId, { nombre, descripcion, publica }) => {
  const updates = { updatedAt: serverTimestamp() };
  if (nombre     !== undefined) updates.nombre      = nombre.trim();
  if (descripcion !== undefined) updates.descripcion = descripcion.trim();
  if (publica    !== undefined) updates.publica     = publica;
  await updateDoc(doc(db, 'recetarios', recetarioId), updates);
};

// ── Eliminar recetario ────────────────────────────────────────────────
export const deleteRecetario = async (recetarioId, userId) => {
  await deleteDoc(doc(db, 'recetarios', recetarioId));
  await updateDoc(doc(db, 'users', userId), { recetariosIds: arrayRemove(recetarioId) });
};

// ── Habilitar acceso por enlace (link sharing) ──────────────────
export const enableLinkShare = async (recetarioId) => {
  await updateDoc(doc(db, 'recetarios', recetarioId), {
    enlaceCompartido: true,
    updatedAt: serverTimestamp(),
  });
};

// ── Añadir co-owner (editor) ────────────────────────────────────────
export const addCoOwner = async (recetarioId, coOwnerId, senderId, recetarioNombre) => {
  await updateDoc(doc(db, 'recetarios', recetarioId), {
    coOwnerIds: arrayUnion(coOwnerId),
    sharedWithIds: arrayRemove(coOwnerId),
    updatedAt: serverTimestamp(),
  });
  if (senderId && coOwnerId !== senderId) {
    await addDoc(collection(db, 'notifications'), {
      type: 'recetario_compartido',
      recipientId: coOwnerId,
      senderId,
      recetarioId,
      recetarioNombre: recetarioNombre || '',
      role: 'editor',
      read: false,
      timestamp: serverTimestamp(),
    });
  }
};

// ── Añadir usuario como lector (solo lectura) ──────────────────────
export const addSharedUser = async (recetarioId, targetUserId, senderId, recetarioNombre) => {
  await updateDoc(doc(db, 'recetarios', recetarioId), {
    sharedWithIds: arrayUnion(targetUserId),
    updatedAt: serverTimestamp(),
  });
  if (senderId && targetUserId !== senderId) {
    await addDoc(collection(db, 'notifications'), {
      type: 'recetario_compartido',
      recipientId: targetUserId,
      senderId,
      recetarioId,
      recetarioNombre: recetarioNombre || '',
      role: 'lector',
      read: false,
      timestamp: serverTimestamp(),
    });
  }
};
